/** Notes endpoints — thin wrappers around the FastAPI backend. */

import { ApiClient } from "./client";
import type { TagResponse } from "./tags";

export type { TagResponse };

export type NoteColor =
  | "purple"
  | "blue"
  | "green"
  | "orange"
  | "yellow"
  | "aqua"
  | "red"
  | "fg";

export const NOTE_COLORS: readonly NoteColor[] = [
  "purple",
  "blue",
  "green",
  "orange",
  "yellow",
  "aqua",
  "red",
  "fg",
];

/** Caller-relative scope for ``GET /notes``. ``"mine"`` returns only
 *  notes the caller owns, ``"shared"`` only notes shared *with* the
 *  caller, ``"all"`` (default) returns both. */
export type NotesScope = "mine" | "shared" | "all";

/** Embedded payload that decorates a note when the caller is a viewer
 *  rather than the owner. ``null`` on owner-owned rows. */
export interface SharedByView {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

/** Effective access of the caller on a shared note. ``"owner"`` is the
 *  implicit access of the row's owner; only viewers carry ``"view"`` /
 *  ``"edit"``. */
export type EffectiveAccess = "view" | "edit" | "owner";

export interface NoteResponse {
  id: string;
  user_id: string;
  content: string;
  tags: TagResponse[];
  created_at: string;
  updated_at: string;
  color: NoteColor | null;
  /** Owner of the note — same as ``user_id`` for backwards compat,
   *  surfaced explicitly so the UI doesn't conflate "the API caller"
   *  with "the owner". */
  owner_id: string;
  /** Non-null on notes shared *with* the caller. Null for notes the
   *  caller owns. */
  shared_by: SharedByView | null;
  /** Effective access the caller has on this row. */
  my_access: EffectiveAccess;
}

export interface NoteListResponse {
  notes: NoteResponse[];
  next_cursor: string | null;
}

export interface CreateNotePayload {
  content: string;
  tags?: string[];
  color?: NoteColor | null;
}

export interface UpdateNotePayload {
  content?: string;
  tags?: string[];
  /** Omit to leave existing; ``null`` clears (auto). */
  color?: NoteColor | null;
}

export const notesApi = (client: ApiClient) => ({
  list(
    opts: {
      limit?: number;
      cursor?: string | null;
      q?: string;
      scope?: NotesScope;
    } = {},
  ): Promise<NoteListResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (opts.limit !== undefined) query.limit = opts.limit;
    if (opts.cursor) query.cursor = opts.cursor;
    if (opts.q) query.q = opts.q;
    if (opts.scope !== undefined) query.scope = opts.scope;
    return client.request<NoteListResponse>("/notes", { method: "GET", query });
  },

  get(noteId: string): Promise<NoteResponse> {
    return client.request<NoteResponse>(`/notes/${noteId}`, { method: "GET" });
  },

  create(payload: CreateNotePayload): Promise<NoteResponse> {
    return client.request<NoteResponse>("/notes", { method: "POST", json: payload });
  },

  update(noteId: string, payload: UpdateNotePayload): Promise<NoteResponse> {
    return client.request<NoteResponse>(`/notes/${noteId}`, { method: "PATCH", json: payload });
  },

  delete(noteId: string): Promise<void> {
    return client.request<void>(`/notes/${noteId}`, { method: "DELETE" });
  },
});
